import React from 'react';
import PropTypes from 'prop-types';
import { Briefcase } from 'lucide-react';
import {
  FormInput,
  FormSelect,
  DateInput,
  CurrencyInput,
  SectionHeader,
  FormNavigation,
  FormRow,
  CardSelect,
} from '../FormComponents';

const employmentStatusOptions = [
  { value: 'employed', label: 'Employed', description: 'W-2 employee' },
  { value: 'self-employed', label: 'Self-Employed', description: 'Own business' },
  { value: 'retired', label: 'Retired', description: 'Not working' },
  { value: 'other', label: 'Other', description: 'Multiple sources' },
];

const yearsOptions = [
  { value: '0', label: 'Less than 1 year' },
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3-5 years' },
  { value: '5', label: '5-10 years' },
  { value: '10', label: '10+ years' },
];

export default function Employment({ data, onChange, onNext, onBack }) {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const isEmployed = data.employmentStatus === 'employed' || data.employmentStatus === 'self-employed';

  // Much simpler validation - just check employment status is selected
  // For employed: require employer name, job title, start date, and monthly income
  let isValid = !!data.employmentStatus;

  if (isEmployed) {
    isValid = !!(
      data.employerName &&
      data.jobTitle &&
      data.startDate &&
      data.monthlyIncome
    );
  }

  return (
    <div>
      <SectionHeader
        title="Employment Information"
        subtitle="Tell us about your current employment"
        icon={<Briefcase size={28} />}
      />

      <CardSelect
        label="Employment Status"
        value={data.employmentStatus}
        onChange={(value) => updateField('employmentStatus', value)}
        options={employmentStatusOptions}
        required
        columns={4}
      />

      {isEmployed && (
        <>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#e2e8f0', marginBottom: '16px' }}>
            Current {data.employmentStatus === 'self-employed' ? 'Business' : 'Employer'}
          </h3>

          <FormRow>
            <FormInput
              label={data.employmentStatus === 'self-employed' ? 'Business Name' : 'Employer Name'}
              value={data.employerName || ''}
              onChange={(value) => updateField('employerName', value)}
              placeholder={data.employmentStatus === 'self-employed' ? 'Your Business LLC' : 'Acme Corporation'}
              required
            />
            <FormInput
              label="Position/Title"
              value={data.jobTitle || ''}
              onChange={(value) => updateField('jobTitle', value)}
              placeholder={data.employmentStatus === 'self-employed' ? 'Owner' : 'Software Engineer'}
              required
            />
          </FormRow>

          <FormRow>
            <FormInput
              label="Employer Address"
              value={data.employerAddress || ''}
              onChange={(value) => updateField('employerAddress', value)}
              placeholder="123 Business Blvd, City, ST 12345"
            />
            <PhoneInputWrapper
              label="Employer Phone"
              value={data.employerPhone || ''}
              onChange={(value) => updateField('employerPhone', value)}
            />
          </FormRow>

          <FormRow>
            <DateInput
              label="Start Date"
              value={data.startDate || ''}
              onChange={(value) => updateField('startDate', value)}
              required
            />
            <FormSelect
              label="Years in this Line of Work"
              value={data.yearsAtJob || ''}
              onChange={(value) => updateField('yearsAtJob', value)}
              options={yearsOptions}
              placeholder="Select duration"
            />
          </FormRow>

          <CurrencyInput
            label="Monthly Gross Income (before taxes)"
            value={data.monthlyIncome || ''}
            onChange={(value) => updateField('monthlyIncome', value)}
            required
            placeholder="$5,000"
          />
        </>
      )}

      {data.employmentStatus === 'retired' && (
        <div
          style={{
            padding: '20px',
            borderRadius: '12px',
            background: 'rgba(56, 189, 248, 0.1)',
            border: '1px solid rgba(56, 189, 248, 0.2)',
            marginBottom: '20px',
          }}
        >
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px' }}>
            As a retiree, you'll be asked about your retirement income sources in the next section.
          </p>
        </div>
      )}

      {data.employmentStatus === 'other' && (
        <FormInput
          label="Please describe your employment situation"
          value={data.employmentDescription || ''}
          onChange={(value) => updateField('employmentDescription', value)}
          placeholder="e.g., Part-time work, seasonal employment, etc."
        />
      )}

      <FormNavigation
        onBack={onBack}
        onNext={onNext}
        isValid={isValid}
      />
    </div>
  );
}

// Simple phone input wrapper since we imported CurrencyInput
function PhoneInputWrapper({ label, value, onChange }) {
  const [focused, setFocused] = React.useState(false);

  const formatPhone = (val) => {
    const digits = (val || '').replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '13px',
          fontWeight: 600,
          color: focused ? '#38bdf8' : '#94a3b8',
          transition: 'color 0.2s ease',
        }}
      >
        {label}
      </label>
      <input
        type="tel"
        inputMode="tel"
        value={formatPhone(value)}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
        placeholder="(555) 555-5555"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%',
          padding: '14px 16px',
          borderRadius: '12px',
          border: '1px solid rgba(148, 163, 184, 0.3)',
          background: 'rgba(255, 255, 255, 0.05)',
          color: '#e2e8f0',
          fontSize: '15px',
          outline: 'none',
          transition: 'all 0.2s ease',
          boxSizing: 'border-box',
          ...(focused ? {
            borderColor: 'rgba(56, 189, 248, 0.6)',
            boxShadow: '0 0 0 3px rgba(56, 189, 248, 0.15)',
          } : {}),
        }}
      />
    </div>
  );
}

Employment.propTypes = {
  data: PropTypes.shape({
    employmentStatus: PropTypes.string,
    employerName: PropTypes.string,
    jobTitle: PropTypes.string,
    employerAddress: PropTypes.string,
    employerPhone: PropTypes.string,
    startDate: PropTypes.string,
    yearsAtJob: PropTypes.string,
    monthlyIncome: PropTypes.string,
    prevEmployerName: PropTypes.string,
    prevJobTitle: PropTypes.string,
    prevStartDate: PropTypes.string,
    prevEndDate: PropTypes.string,
    employmentDescription: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};
